import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { 
  Sparkles, 
  X, 
  Copy, 
  Check, 
  RefreshCw, 
  Loader2,
  Wand2,
  ArrowRight,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/client';
import { cn } from '../../utils/cn';

interface ReplyDraft {
  content: string;
  subject: string;
  tone: string;
  tokensUsed?: number;
}

interface AiReplyDraftProps {
  accountId: string;
  messageId: string;
  onClose: () => void;
  onUse?: (content: string, subject: string) => void;
}

type Tone = 'professional' | 'friendly' | 'casual' | 'formal';
type Length = 'short' | 'medium' | 'long';

const TONE_OPTIONS: Array<{ value: Tone; label: string; emoji: string }> = [
  { value: 'professional', label: 'Professionell', emoji: '💼' },
  { value: 'friendly', label: 'Freundlich', emoji: '😊' },
  { value: 'casual', label: 'Locker', emoji: '✌️' },
  { value: 'formal', label: 'Formell', emoji: '🎩' },
];

const LENGTH_OPTIONS: Array<{ value: Length; label: string }> = [
  { value: 'short', label: 'Kurz' },
  { value: 'medium', label: 'Mittel' },
  { value: 'long', label: 'Ausführlich' },
];

export default function AiReplyDraft({ accountId, messageId, onClose, onUse }: AiReplyDraftProps) {
  const [selectedTone, setSelectedTone] = useState<Tone>('professional');
  const [selectedLength, setSelectedLength] = useState<Length>('medium');
  const [customInstructions, setCustomInstructions] = useState('');
  const [draft, setDraft] = useState<ReplyDraft | null>(null);
  const [copied, setCopied] = useState(false);
  
  // Generate draft mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('ai/draft-reply', {
        json: {
          accountId,
          messageId,
          options: {
            tone: selectedTone,
            length: selectedLength,
            language: 'auto',
            instructions: customInstructions || undefined,
          },
        },
      }).json<{ draft: ReplyDraft }>();
      return response.draft;
    },
    onSuccess: (data) => {
      setDraft(data);
    },
    onError: (error: any) => {
      const message = error?.message || 'Entwurf konnte nicht erstellt werden';
      toast.error(message);
    },
  });
  
  // Improve draft mutation
  const improveMutation = useMutation({
    mutationFn: async (improvement: string) => {
      if (!draft) throw new Error('Kein Entwurf vorhanden');
      
      const response = await api.post('ai/improve-draft', {
        json: {
          content: draft.content,
          improvement,
        },
      }).json<{ content: string }>();
      return response.content;
    },
    onSuccess: (content) => {
      if (draft) {
        setDraft({ ...draft, content });
        toast.success('Entwurf verbessert');
      }
    },
    onError: () => {
      toast.error('Verbesserung fehlgeschlagen');
    },
  });
  
  const handleCopy = () => {
    if (draft) {
      navigator.clipboard.writeText(draft.content);
      setCopied(true);
      toast.success('In Zwischenablage kopiert');
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const handleUse = () => {
    if (draft && onUse) {
      onUse(draft.content, draft.subject);
      onClose();
    }
  };
  
  const isLoading = generateMutation.isPending || improveMutation.isPending;
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-2xl rounded-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col"
        style={{ backgroundColor: 'var(--bg-primary)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-5 py-4"
          style={{ 
            borderBottom: '1px solid var(--border-default)',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
          }}
        >
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ 
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              }}
            >
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                AI Antwort-Entwurf
              </h2>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Lass KI eine Antwort für dich schreiben
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {!draft ? (
            // Configuration View
            <div className="space-y-5">
              {/* Tone Selection */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Tonalität
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {TONE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setSelectedTone(opt.value)}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                        selectedTone === opt.value && 'ring-2 ring-offset-2'
                      )}
                      style={{
                        backgroundColor: selectedTone === opt.value 
                          ? 'rgba(99, 102, 241, 0.15)' 
                          : 'var(--bg-secondary)',
                        border: `1px solid ${selectedTone === opt.value ? 'var(--brand-primary)' : 'var(--border-default)'}`,
                        color: selectedTone === opt.value ? 'var(--brand-primary)' : 'var(--text-secondary)',
                        '--tw-ring-color': 'var(--brand-primary)',
                        '--tw-ring-offset-color': 'var(--bg-primary)',
                      } as any}
                    >
                      <span>{opt.emoji}</span>
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Length Selection */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Länge
                </label>
                <div className="flex gap-2">
                  {LENGTH_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setSelectedLength(opt.value)}
                      className={cn(
                        'flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all'
                      )}
                      style={{
                        backgroundColor: selectedLength === opt.value 
                          ? 'rgba(99, 102, 241, 0.15)' 
                          : 'var(--bg-secondary)',
                        border: `1px solid ${selectedLength === opt.value ? 'var(--brand-primary)' : 'var(--border-default)'}`,
                        color: selectedLength === opt.value ? 'var(--brand-primary)' : 'var(--text-secondary)',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Custom Instructions */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Zusätzliche Anweisungen (optional)
                </label>
                <textarea
                  value={customInstructions}
                  onChange={e => setCustomInstructions(e.target.value)}
                  placeholder="z.B. 'Erwähne das Meeting nächste Woche' oder 'Frage nach dem Projektstatus'"
                  className="w-full px-4 py-3 rounded-lg text-sm resize-none"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--text-primary)',
                  }}
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs mt-1 text-right" style={{ color: 'var(--text-muted)' }}>
                  {customInstructions.length}/500
                </p>
              </div>
              
              {/* Generate Button */}
              <button
                onClick={() => generateMutation.mutate()}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-semibold transition-all disabled:opacity-50"
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generiere...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    Entwurf generieren
                  </>
                )}
              </button>
            </div>
          ) : (
            // Draft View
            <div className="space-y-4">
              {/* Draft Content */}
              <div 
                className="rounded-xl p-4"
                style={{ 
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-default)',
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium px-2 py-1 rounded-full" style={{
                    backgroundColor: 'rgba(99, 102, 241, 0.15)',
                    color: 'var(--brand-primary)',
                  }}>
                    {TONE_OPTIONS.find(t => t.value === draft.tone)?.emoji} {TONE_OPTIONS.find(t => t.value === draft.tone)?.label}
                  </span>
                  {draft.tokensUsed && (
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {draft.tokensUsed} Tokens
                    </span>
                  )}
                </div>
                
                <div 
                  className="whitespace-pre-wrap text-sm leading-relaxed"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {draft.content}
                </div>
              </div>
              
              {/* Quick Improvements */}
              <div>
                <label className="block text-xs font-medium mb-2" style={{ color: 'var(--text-muted)' }}>
                  Schnelle Anpassungen
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'shorter', label: 'Kürzer' },
                    { key: 'longer', label: 'Länger' },
                    { key: 'more_professional', label: 'Professioneller' },
                    { key: 'more_friendly', label: 'Freundlicher' },
                    { key: 'fix_grammar', label: 'Grammatik prüfen' },
                  ].map(btn => (
                    <button
                      key={btn.key}
                      onClick={() => improveMutation.mutate(btn.key)}
                      disabled={isLoading}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                      style={{
                        backgroundColor: 'var(--bg-tertiary)',
                        border: '1px solid var(--border-default)',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => setDraft(null)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <RefreshCw className="w-4 h-4" />
                  Neu generieren
                </button>
                
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                  Kopieren
                </button>
                
                {onUse && (
                  <button
                    onClick={handleUse}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-white transition-all"
                    style={{
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    }}
                  >
                    <MessageSquare className="w-4 h-4" />
                    Verwenden
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Export a trigger button component
export function AiReplyButton({ 
  onClick, 
  size = 'default' 
}: { 
  onClick: () => void;
  size?: 'small' | 'default';
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 font-medium rounded-lg transition-all hover:opacity-90',
        size === 'small' ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'
      )}
      style={{
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        color: 'white',
      }}
      title="AI Antwort-Entwurf generieren"
    >
      <Sparkles className={size === 'small' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
      AI Antwort
    </button>
  );
}
